import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { GameMobileLayout } from '../game/styles/mobile-styles'
import { subscribeToLeaderboard, LeaderboardEntry as LeaderboardEntryType } from '../../services/leaderboardService'

// Helper function to format time from seconds to MM:SS
const formatTime = (timeInSeconds?: number): string => {
  if (!timeInSeconds || timeInSeconds === 0) return '--:--'
  const minutes = Math.floor(timeInSeconds / 60)
  const seconds = timeInSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

// Styled Components - Leaderboard Layout Only
const LeaderboardContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 1024px) {
    padding: 0 16px;
  }
`

const TableHeader = styled.div`
  background: rgba(0, 0, 0, 0.8);
  border-bottom: 1px solid #374151;
  position: sticky;
  top: 0;
  z-index: 20;
`

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 16px 0;

  @media (min-width: 768px) {
    margin-bottom: 24px;
  }
`

const BackButton = styled.button`
  position: absolute;
  left: 16px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }

  @media (min-width: 768px) {
    left: 0;

    svg {
      width: 24px;
      height: 24px;
    }

    span {
      font-size: 16px;
    }
  }
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  color: #d1d5db;
  font-size: 14px;
`

const HeaderCell = styled.div<{ flex?: string; hideOnMobile?: boolean }>`
  padding: 12px 16px;
  ${(props) => props.flex && `flex: ${props.flex};`}
  ${(props) => props.flex === 'none' && 'flex-shrink: 0; min-width: 0;'}
  
  ${(props) =>
    props.hideOnMobile &&
    `
    display: none;
    
    @media (min-width: 768px) {
      display: block;
    }
  `}
`

const LeaderboardList = styled.div`
  overflow-y: auto;
  flex: 1;
  padding-bottom: 80px;

  @media (min-width: 1024px) {
    padding-bottom: 16px;
  }
`

const LeaderboardRowContainer = styled.div`
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(55, 65, 81, 0.2);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`

const RankSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  min-width: 100px;
  flex-shrink: 0;
`

const TrophyIcon = styled.div`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TrophySvg = styled.svg<{ isTop?: boolean }>`
  width: 24px;
  height: 24px;
  color: ${(props) => (props.isTop ? '#fbbf24' : '#9ca3af')};
`

const RankNumber = styled.span`
  color: white;
  font-size: 14px;
  min-width: 0;
`

const RankChangeIndicator = styled.div<{ change: 'up' | 'down' | 'same' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  ${(props) => {
    switch (props.change) {
      case 'up':
        return `
          color: #10b981;
          background-color: rgba(16, 185, 129, 0.1);
          border-radius: 50%;
        ` // green
      case 'down':
        return `
          color: #ef4444;
          background-color: rgba(239, 68, 68, 0.1);
          border-radius: 50%;
        ` // red
      case 'same':
        return `
          color: #6b7280;
          background-color: rgba(107, 114, 128, 0.1);
          border-radius: 50%;
        ` // gray

      default:
        return 'color: #6b7280;'
    }
  }}
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: white;
  font-size: 16px;
`

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #9ca3af;
  font-size: 16px;
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
`

const PlayerSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  flex: 1;
  min-width: 0;
`

const Avatar = styled.div<{ bgColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  background-color: ${(props) => props.bgColor};

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
  }
`

const PlayerName = styled.span`
  color: white;
  font-weight: bold;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`

const ScoreSection = styled.div`
  padding: 16px;
  flex-shrink: 0;
`

const Score = styled.span`
  color: white;
  font-weight: bold;
  font-size: 14px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`

const RankIndicationSection = styled.div`
  display: none;
  padding: 16px;
  flex-shrink: 0;
  min-width: 60px;
  justify-content: center;

  @media (min-width: 768px) {
    display: flex;
  }
`

const TimeSection = styled.div`
  display: none;
  padding: 16px;
  flex-shrink: 0;
  min-width: 80px;

  @media (min-width: 768px) {
    display: block;
  }
`

const Time = styled.span`
  color: #9ca3af;
  font-size: 12px;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`

const LoadMoreButton = styled.button`
  background: #2563eb;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  margin: 24px auto;
  display: block;

  &:hover {
    background: #1d4ed8;
  }
`

interface LeaderboardRowProps {
  entry: LeaderboardEntryType
}

function LeaderboardRow({ entry }: LeaderboardRowProps) {
  const { rank, fullName, initials, score, color, rankChange, totalTime } = entry

  window.chaport.q('hideLauncher')

  const renderRankChangeIcon = () => {
    switch (rankChange) {
      case 'up':
        return (
          <RankChangeIndicator change="up" title="Rank improved">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L10 4.414 4.707 9.707a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </RankChangeIndicator>
        )
      case 'down':
        return (
          <RankChangeIndicator change="down" title="Rank dropped">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L10 15.586l5.293-5.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </RankChangeIndicator>
        )

      case 'same':
      default:
        return (
          <RankChangeIndicator change="same" title="Rank unchanged">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </RankChangeIndicator>
        )
    }
  }

  return (
    <LeaderboardRowContainer>
      <RankSection>
        <TrophyIcon>
          <TrophySvg isTop={rank <= 3} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </TrophySvg>
        </TrophyIcon>
        <RankNumber>{rank}</RankNumber>
      </RankSection>

      <PlayerSection>
        <Avatar bgColor={color}>{initials}</Avatar>
        <PlayerName>{fullName}</PlayerName>
      </PlayerSection>

      <RankIndicationSection>{renderRankChangeIcon()}</RankIndicationSection>

      <ScoreSection>
        <Score>+{score}</Score>
      </ScoreSection>

      <TimeSection>
        <Time>{formatTime(totalTime)}</Time>
      </TimeSection>
    </LeaderboardRowContainer>
  )
}

export default function Leaderboard() {
  // Configurable limit for leaderboard display
  const LEADERBOARD_LIMIT = 30
  const history = useHistory()

  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntryType[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    const unsubscribe = subscribeToLeaderboard((data) => {
      setLeaderboardData(data)
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const displayedData = showAll ? leaderboardData : leaderboardData.slice(0, LEADERBOARD_LIMIT)

  const handleBackToLobby = () => {
    history.push('/lobby')
  }

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <div>Loading leaderboard...</div>
        </LoadingContainer>
      )
    }

    if (leaderboardData.length === 0) {
      return (
        <EmptyContainer>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              clipRule="evenodd"
            />
          </svg>
          <div>No participants yet</div>
          <div style={{ fontSize: '14px', marginTop: '4px' }}>Start playing the quiz to appear on the leaderboard!</div>
        </EmptyContainer>
      )
    }

    return (
      <>
        {displayedData.map((entry) => (
          <LeaderboardRow key={entry.id} entry={entry} />
        ))}

        {/* {!showAll && leaderboardData.length > LEADERBOARD_LIMIT && (
          <LoadMoreButton onClick={() => setShowAll(true)}>Show All {leaderboardData.length} Entries</LoadMoreButton>
        )} */}
      </>
    )
  }

  return (
    <GameMobileLayout id="game-container" styled={{ padding: '0', backgroundAttachment: 'fixed' }}>
      <HeaderSection>
        <BackButton onClick={handleBackToLobby}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          <span>Back</span>
        </BackButton>
        <Title>Leaderboard</Title>
      </HeaderSection>
      <LeaderboardContainer>
        {/* Table Header */}
        <TableHeader>
          <HeaderRow>
            <HeaderCell flex="none">Place</HeaderCell>
            <HeaderCell flex="1">Name</HeaderCell>
            <HeaderCell flex="none" hideOnMobile>
              Rank
            </HeaderCell>
            <HeaderCell flex="none">Score</HeaderCell>
            <HeaderCell flex="none" hideOnMobile>
              Time
            </HeaderCell>
          </HeaderRow>
        </TableHeader>

        {/* Leaderboard List */}
        <LeaderboardList>{renderContent()}</LeaderboardList>
      </LeaderboardContainer>
    </GameMobileLayout>
  )
}
